import React from 'react';
import { useIntl } from 'react-intl';
import { Presentation } from 'lucide-react';
import PersonName, { Avatar } from './PersonName';
import { isElement, isEntry, layoutDay, peopleByRole, pick, placeOf, timeRange } from './programModel';
import {
  AvatarStack, Block, BlockHead, BlockHeadText, Child, ChildTime, DayHeading, Discussion, ElementHeading, ElementQuote, ElementText, EntryTitle, Eyebrow,
  Focus, FullRow, GroupHeading, Label, MetaRow, PeopleBlock, RoleLabel, RoleLine, RoomHeading, Slot, SlotAfter,
  SlotEntries, SlotTime, SlidesLink, StackMore, Subtitle, Tag, UntimedHeading, UntimedList, VenueNote,
} from './programStyles';

const API = process.env.REACT_APP_API_URL || '';

// Decision 5 of §7.6: a published deck, downloaded from the public slides route.
export const Slides = ({ url, small }) => {
  const intl = useIntl();
  return (
    <SlidesLink href={`${API}${url}`} $small={small} download>
      <Presentation size={small ? 14 : 16} aria-hidden="true" />
      {intl.formatMessage({ id: 'program.public.slides', defaultMessage: 'Slides' })}
    </SlidesLink>
  );
};

// Kinds printed as quiet rows rather than as a block card.
const QUIET_KINDS = new Set(['BREAK', 'ARRIVAL', 'SOCIAL']);

export const entryDomId = (id) => `program-entry-${id}`;

const roomName = (language, room) => (room ? pick(language, room.nameDe, room.nameEn) : '');

const venueText = (venue) => (venue ? [venue.name, venue.city].filter(Boolean).join(', ') : '');

// A display element (design §4.4.1) in its place among entries.
export const DisplayElement = ({ element, language }) => {
  const text = pick(language, element.textDe, element.textEn);
  if (!text) return null;
  const box = element.style === 'HIGHLIGHTED_BOX';
  if (element.kind === 'QUOTE') return <ElementQuote $box={box}>{text}</ElementQuote>;
  if (element.kind === 'HEADING') return <ElementHeading $box={box}>{text}</ElementHeading>;
  return <ElementText $box={box}>{text}</ElementText>;
};

const People = ({ people, language, selection, onSelectPerson, print }) => {
  const intl = useIntl();
  const roles = peopleByRole(people);
  if (roles.length === 0) return null;
  return (
    <PeopleBlock>
      {roles.map(({ role, count, groups }) => (
        <RoleLine key={role}>
          <RoleLabel>
            {intl.formatMessage({ id: `program.public.role.${role}`, defaultMessage: role }, { count })}
          </RoleLabel>
          {groups.map((group, gi) => (
            <React.Fragment key={`${group.label || ''}-${gi}`}>
              {group.label && <GroupHeading>{group.label}</GroupHeading>}
              {group.people.map((p, i) => (
                <React.Fragment key={`${p.name}-${i}`}>
                  {i > 0 && print && '; '}
                  {p.tba ? (
                    <Label>{p.name}</Label>
                  ) : (
                    <>
                      <PersonName
                        person={p}
                        print={print}
                        highlight={selection != null && selection === p.personId}
                        onShowEntries={onSelectPerson && p.personId != null ? () => onSelectPerson(p.personId) : undefined}
                      />
                      {p.slidesUrl && <>{' '}<Slides url={p.slidesUrl} small /></>}
                    </>
                  )}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </RoleLine>
      ))}
    </PeopleBlock>
  );
};

// Everyone named in an entry and its children, once each (a person key, never a label).
const namedIn = (entry) => {
  const seen = new Map();
  const walk = (n) => {
    (n.people || []).forEach((p) => { if (!p.tba && p.personId != null && !seen.has(p.personId)) seen.set(p.personId, p); });
    (n.children || []).filter(isEntry).forEach(walk);
  };
  walk(entry);
  return [...seen.values()];
};

const STACK_MAX = 5;

// The faces of a block, overlapping, next to its title. Decoration: every name is printed below.
const Faces = ({ entry }) => {
  const named = namedIn(entry);
  if (named.length < 2) return null;
  return (
    <AvatarStack aria-hidden="true">
      {named.slice(0, STACK_MAX).map((p) => <Avatar key={p.personId} name={p.name} photoUrl={p.photoUrl} size={40} />)}
      {named.length > STACK_MAX && <StackMore>{`+${named.length - STACK_MAX}`}</StackMore>}
    </AvatarStack>
  );
};

const EntryBody = ({ entry, depth, language, ctx }) => {
  const intl = useIntl();
  const title = pick(language, entry.titleDe, entry.titleEn);
  const subtitle = pick(language, entry.subtitleDe, entry.subtitleEn);
  const label = pick(language, entry.labelDe, entry.labelEn);
  const series = depth === 0 ? pick(language, entry.seriesLabelDe, entry.seriesLabelEn) : '';
  const focus = depth === 0 ? pick(language, entry.focusDe, entry.focusEn) : '';
  const head = (
    <>
      {(series || label) && <Eyebrow>{[series, label].filter(Boolean).join(' · ')}</Eyebrow>}
      <EntryTitle $depth={depth}>
        {depth > 0 && timeRange(entry.startTime, entry.endTime) && (
          <ChildTime $depth={depth}>{timeRange(entry.startTime, entry.endTime)}</ChildTime>
        )}
        {title}
      </EntryTitle>
      {subtitle && <Subtitle>{subtitle}</Subtitle>}
      {focus && <Focus>{focus}</Focus>}
    </>
  );
  return (
    <>
      {depth === 0 ? (
        <BlockHead>
          <BlockHeadText>{head}</BlockHeadText>
          {!ctx.print && <Faces entry={entry} />}
        </BlockHead>
      ) : head}
      {entry.slidesUrl && <div><Slides url={entry.slidesUrl} /></div>}
      <People
        people={entry.people}
        language={language}
        selection={ctx.selection}
        onSelectPerson={ctx.onSelectPerson}
        print={ctx.print}
      />
      {entry.discussionMinutes != null && (
        <Discussion>
          {intl.formatMessage(
            { id: 'program.public.discussion', defaultMessage: 'Discussion: {minutes} min' },
            { minutes: entry.discussionMinutes },
          )}
        </Discussion>
      )}
      <Children nodes={entry.children} depth={depth + 1} language={language} ctx={ctx} />
    </>
  );
};

const Children = ({ nodes, depth, language, ctx }) => (
  <>
    {(nodes || []).map((n) => {
      if (isElement(n)) return <DisplayElement key={`el-${n.id}`} element={n} language={language} />;
      if (!isEntry(n)) return null;
      return (
        <Child
          key={n.id}
          id={entryDomId(n.id)}
          $depth={depth}
          $highlight={ctx.highlighted.has(n.id)}
          data-highlighted={ctx.highlighted.has(n.id) || undefined}
        >
          <EntryBody entry={n} depth={depth} language={language} ctx={ctx} />
        </Child>
      );
    })}
  </>
);

// A top-level block: time range, room tag, another venue as a note, then the entry and its children.
// inSlot: the slot already prints the start time, so a block without an end time repeats nothing.
const TopBlock = ({ entry, program, language, ctx, showRoom = true, inSlot = false }) => {
  const intl = useIntl();
  const { room, venue } = placeOf(program, entry.roomId);
  const range = inSlot && !entry.endTime ? '' : timeRange(entry.startTime, entry.endTime);
  return (
    <Block
      id={entryDomId(entry.id)}
      $quiet={QUIET_KINDS.has(entry.kind)}
      $highlight={ctx.highlighted.has(entry.id)}
      data-highlighted={ctx.highlighted.has(entry.id) || undefined}
    >
      {(range || (showRoom && room) || venue) && (
        <MetaRow>
          {range && <span>{range}</span>}
          {showRoom && room && <Tag>{roomName(language, room)}</Tag>}
          {venue && (
            <VenueNote>
              {intl.formatMessage({ id: 'program.public.venue', defaultMessage: 'at {venue}' }, { venue: venueText(venue) })}
            </VenueNote>
          )}
        </MetaRow>
      )}
      <EntryBody entry={entry} depth={0} language={language} ctx={ctx} />
    </Block>
  );
};

const dayLabel = (intl, language, day) => {
  const label = pick(language, day.labelDe, day.labelEn);
  if (label) return label;
  if (!day.day) return '';
  const [y, m, d] = String(day.day).split('-').map(Number);
  return intl.formatDate(new Date(y, m - 1, d), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

export { dayLabel };

// One day of the program: top-level display elements, the timed slots in time order (parallel blocks
// side by side on wide screens, stacked on phones), then the untimed blocks per room. ctx.afterEntry
// (ProgramSectionView) puts the sponsor pages after the slot of the entry the admin chose.
const ProgramDay = ({ program, day, language, ctx, showHeading }) => {
  const intl = useIntl();
  const { before, slots, untimed } = layoutDay(program, day);
  return (
    <div>
      {showHeading && dayLabel(intl, language, day) && <DayHeading>{dayLabel(intl, language, day)}</DayHeading>}
      {before.map((el) => <DisplayElement key={`el-${el.id}`} element={el} language={language} />)}
      {slots.map((slot, i) => (
        <React.Fragment key={slot.time}>
          <Slot>
            <SlotTime $covered={slot.entries.every((e) => e.endTime)}>{slot.time}</SlotTime>
            <SlotEntries $columns={slot.entries.length}>
              {slot.entries.map((e) => (
                <TopBlock key={e.id} entry={e} program={program} language={language} ctx={ctx} inSlot />
              ))}
            </SlotEntries>
          </Slot>
          {slot.after.length > 0 && (
            <SlotAfter>
              {slot.after.map((el) => <DisplayElement key={`el-${el.id}`} element={el} language={language} />)}
            </SlotAfter>
          )}
          {ctx.afterEntry && slot.entries.map((e) => <React.Fragment key={`after-${e.id}`}>{ctx.afterEntry(e.id)}</React.Fragment>)}
        </React.Fragment>
      ))}
      {untimed.length > 0 && (
        <>
          <UntimedHeading>
            {intl.formatMessage({ id: 'program.public.untimed', defaultMessage: 'Without a fixed time' })}
          </UntimedHeading>
          {untimed.map(({ roomId, items }) => {
            const { room } = placeOf(program, roomId);
            return (
              <div key={roomId ?? 'none'}>
                <RoomHeading>
                  {room
                    ? roomName(language, room)
                    : intl.formatMessage({ id: 'program.public.noRoom', defaultMessage: 'Other' })}
                </RoomHeading>
                <UntimedList>
                  {items.map(({ entry: e, after }) => (
                    <React.Fragment key={e.id}>
                      <TopBlock entry={e} program={program} language={language} ctx={ctx} showRoom={false} />
                      {after.length > 0 && (
                        <FullRow>
                          {after.map((el) => <DisplayElement key={`el-${el.id}`} element={el} language={language} />)}
                        </FullRow>
                      )}
                      {ctx.afterEntry?.(e.id) && <FullRow>{ctx.afterEntry(e.id)}</FullRow>}
                    </React.Fragment>
                  ))}
                </UntimedList>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default ProgramDay;
